import React, { useContext } from 'react';

import { app, media } from 'context';
import { SIDEBAR_PRIMARY_WIDTH } from 'ds/constants';
import { GRAY_50_HEX } from 'shared';

import { DEFAULT_PAGE_SIZE_OPTIONS } from './constants';
import { Pagination } from './types';
import IconButton from '../IconButton';
import Layout from '../Layout';
import Select from '../Select';
import Text from '../Text';

interface TableFooterProps {
  pageSize: number;
  handlePageSizeChange: (value: number) => void;
  currentPage: number;
  handlePageChange: (value: number) => void;
  rows: any[];
  pagination?: Pagination;
  totalCount: number;
  bottom?: number;
}

const TableFooter: React.FC<TableFooterProps> = ({
  pageSize,
  handlePageSizeChange,
  currentPage,
  handlePageChange,
  rows,
  pagination,
  totalCount
}) => {
  const { isMobile } = useContext(media);
  const { bottomNavigationHeight } = useContext(app);

  return (
    <Layout
      position="fixed"
      bottom={isMobile ? bottomNavigationHeight : 0}
      right={0}
      left={!isMobile ? SIDEBAR_PRIMARY_WIDTH : undefined}
      direction="column"
    >
      <Layout
        style={{
          borderTop: `2px solid ${GRAY_50_HEX}`
        }}
        marginX={isMobile ? 24 : 40}
      ></Layout>
      <Layout
        height={isMobile ? 72 : 60}
        color="white"
        align={isMobile ? 'flex-start' : 'center'}
        justify="space-between"
        paddingX={62}
        zIndex={100}
        width={isMobile ? '100vw' : '100%'}
        {...(isMobile ? { paddingTop: 12, paddingBottom: 8 } : {})}
      >
        {pagination ? (
          <>
            <Layout
              align={isMobile ? 'flex-start' : 'center'}
              gap={isMobile ? 4 : 8}
              direction={isMobile ? 'column' : 'row'}
            >
              <Layout direction="row" gap={8} align="center">
                <Text size="body-xs" color="gray-600">
                  Items per page
                </Text>
                <Select
                  size="xs"
                  value={pageSize}
                  onChange={({ value }) => handlePageSizeChange(value)}
                  options={(pagination.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS).map(size => ({
                    label: size.toString(),
                    value: size
                  }))}
                />
              </Layout>
              <Layout direction="row" gap={4}>
                <Text size="body-xs" color="gray-600">
                  {`${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of`}
                </Text>
                <Text size="body-xs" color="gray-600">
                  {totalCount}
                </Text>
                <Text size="body-xs" color="gray-600">
                  items
                </Text>
              </Layout>
            </Layout>
            <Layout align="center" gap={12}>
              <IconButton
                name="leftChevron"
                type="noBackground"
                onClick={() => handlePageChange(currentPage - 1)}
                size="sm"
                disabled={currentPage === 1}
                stroke={2}
              />
              <Text size="body-xs" color="gray-600">
                {currentPage} of {Math.ceil(totalCount / pageSize)}
              </Text>
              <IconButton
                name="rightChevron"
                type="noBackground"
                onClick={() => handlePageChange(currentPage + 1)}
                size="sm"
                disabled={currentPage * pageSize >= totalCount}
                stroke={2}
              />
            </Layout>
          </>
        ) : (
          <Text size="body-xs" color="gray-600">
            {rows.length} items
          </Text>
        )}
      </Layout>
    </Layout>
  );
};

export default TableFooter;
